import React, { useMemo, useRef, useState } from 'react'
import {
    Button,
    IconButton,
    Modal,
    Paper,
    Slide,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import { fade } from '@material-ui/core/styles/colorManipulator'
import AddIcon from '@material-ui/icons/Add'
import CloseIcon from '@material-ui/icons/Close'
import DeleteIcon from '@material-ui/icons/Delete'
import GetAppIcon from '@material-ui/icons/GetApp'
import PublishIcon from '@material-ui/icons/Publish'
import { Container } from '../../components'
import { useAppDispatch } from '../../hooks'
import {
    bulkUpsertChangeOrderConfigsRequest,
    ChangeOrderConfigBulkItem,
} from '../../store'
import { typographyStyle } from '../../theme/typographyStyle'
import { toast } from 'react-toastify'

interface Props {
    open: boolean
    handleClose: () => void
}

const NAME_MAX_LEN = 30
const DESCRIPTION_MAX_LEN = 200

interface ConfigRow {
    id: number
    name: string
    description: string
    expense: string
}

type RowField = 'name' | 'description' | 'expense'

let nextId = 1

const createEmptyRow = (): ConfigRow => ({
    id: nextId++,
    name: '',
    description: '',
    expense: '',
})

const TEMPLATE_HEADERS = ['Name', 'Description', 'Expense']

const EXAMPLE_ROW = 'Paint touch-up,Interior wall paint touch-up per room,45.00'

const downloadTemplate = () => {
    const csv = `${TEMPLATE_HEADERS.join(',')}\n${EXAMPLE_ROW}\n`
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'change_order_config_bulk_template.csv'
    a.click()
    URL.revokeObjectURL(url)
}

function parseCSVLine(line: string): string[] {
    const result: string[] = []
    let current = ''
    let inQuotes = false
    for (let i = 0; i < line.length; i++) {
        const c = line[i]
        if (c === '"') {
            inQuotes = !inQuotes
        } else if (c === ',' && !inQuotes) {
            result.push(current.trim())
            current = ''
        } else {
            current += c
        }
    }
    result.push(current.trim())
    return result
}

function normalizeExpenseInput(raw: string): string {
    return raw
        .replace(/^\s*\$\s*/, '')
        .replace(/,/g, '')
        .trim()
}

function parseExpenseToNumber(expense: string): number | undefined {
    const s = normalizeExpenseInput(expense)
    if (s === '') return undefined
    const n = Number(s)
    return Number.isFinite(n) ? n : undefined
}

function rowHasInvalidExpense(r: ConfigRow): boolean {
    const s = normalizeExpenseInput(r.expense)
    if (s === '') return false
    return parseExpenseToNumber(r.expense) === undefined
}

type ParseResult =
    | { ok: true; rows: Omit<ConfigRow, 'id'>[] }
    | { ok: false; reason: 'empty' | 'bad_headers' }

const parseCSV = (text: string): ParseResult => {
    const lines = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean)

    if (lines.length < 2) return { ok: false, reason: 'empty' }

    const header = parseCSVLine(lines[0]).map((h) =>
        h
            .toLowerCase()
            .replace(/\(.*\)/g, '')
            .trim(),
    )

    const nameIdx = header.findIndex(
        (h) => h === 'name' || h.startsWith('name '),
    )
    const descIdx = header.findIndex(
        (h) =>
            h === 'description' || h.startsWith('description') || h === 'desc',
    )
    const expenseIdx = header.findIndex(
        (h) =>
            h === 'expense' ||
            h === 'cost' ||
            h.includes('suggested') ||
            h.includes('price'),
    )

    if (nameIdx === -1 || descIdx === -1 || expenseIdx === -1) {
        return { ok: false, reason: 'bad_headers' }
    }

    const rows = lines
        .slice(1)
        .map((line) => {
            const cols = parseCSVLine(line)
            return {
                name: cols[nameIdx] ?? '',
                description: cols[descIdx] ?? '',
                expense: cols[expenseIdx] ?? '',
            }
        })
        .filter((r) => r.name || r.description || r.expense)

    return { ok: true, rows }
}

function rowToBulkItem(
    trimmedName: string,
    description: string,
    expense: string,
): ChangeOrderConfigBulkItem {
    const item: ChangeOrderConfigBulkItem = { name: trimmedName }
    const desc = description.trim()
    if (desc) {
        item.description = desc
    }
    const n = parseExpenseToNumber(expense)
    if (n !== undefined) {
        item.suggested_add_price = n.toFixed(2)
    }
    return item
}

/** Same as backend: duplicate stripped names collapse; last row wins. */
function mergeRowsForBulk(rows: ConfigRow[]): ChangeOrderConfigBulkItem[] {
    const map = new Map<string, ChangeOrderConfigBulkItem>()
    for (const r of rows) {
        const name = r.name.trim()
        if (!name) {
            continue
        }
        map.set(name, rowToBulkItem(name, r.description, r.expense))
    }
    return Array.from(map.values())
}

interface BulkFormValidation {
    hasPayload: boolean
    bulkSaveCount: number
    partialRowWithoutName: boolean
    hasInvalidExpense: boolean
    hasNameTooLong: boolean
    hasDescriptionTooLong: boolean
    blocking: boolean
}

function computeBulkValidation(rows: ConfigRow[]): BulkFormValidation {
    let partialRowWithoutName = false
    let hasInvalidExpense = false
    let hasNameTooLong = false
    let hasDescriptionTooLong = false

    for (const r of rows) {
        const nameT = r.name.trim()
        if (
            nameT === '' &&
            (r.description.trim() !== '' ||
                normalizeExpenseInput(r.expense) !== '')
        ) {
            partialRowWithoutName = true
        }
        if (rowHasInvalidExpense(r)) {
            hasInvalidExpense = true
        }
        if (nameT.length > NAME_MAX_LEN) {
            hasNameTooLong = true
        }
        if (r.description.trim().length > DESCRIPTION_MAX_LEN) {
            hasDescriptionTooLong = true
        }
    }

    const change_order_configs = mergeRowsForBulk(rows)
    const bulkSaveCount = change_order_configs.length
    const hasPayload = bulkSaveCount > 0

    const blocking =
        !hasPayload ||
        partialRowWithoutName ||
        hasInvalidExpense ||
        hasNameTooLong ||
        hasDescriptionTooLong

    return {
        hasPayload,
        bulkSaveCount,
        partialRowWithoutName,
        hasInvalidExpense,
        hasNameTooLong,
        hasDescriptionTooLong,
        blocking,
    }
}

export const BulkChangeOrderConfigModal = ({ open, handleClose }: Props) => {
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const uploadRef = useRef<HTMLInputElement>(null)

    const [rows, setRows] = useState<ConfigRow[]>([createEmptyRow()])
    const [isSubmitting, setIsSubmitting] = useState(false)

    const validation = useMemo(() => computeBulkValidation(rows), [rows])

    const hasAnyUserInput = useMemo(
        () =>
            rows.some(
                (r) =>
                    r.name.trim() !== '' ||
                    r.description.trim() !== '' ||
                    normalizeExpenseInput(r.expense) !== '',
            ),
        [rows],
    )

    const showIssueBanner = validation.blocking && hasAnyUserInput

    const addRow = () => setRows((prev) => [...prev, createEmptyRow()])

    const removeRow = (id: number) =>
        setRows((prev) =>
            prev.length <= 1 ? prev : prev.filter((r) => r.id !== id),
        )

    const updateRow = (id: number, field: RowField, value: string) => {
        setRows((prev) =>
            prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
        )
    }

    const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = (ev) => {
            const text = (ev.target?.result as string).replace(/^\uFEFF/, '')
            const parsed = parseCSV(text)
            if (!parsed.ok) {
                if (parsed.reason === 'bad_headers') {
                    toast.error(
                        'CSV must include Name, Description, and Expense columns (use the template).',
                    )
                } else if (text.trim().length > 0) {
                    toast.error(
                        'No data rows found. Add at least one row below the header.',
                    )
                }
                return
            }
            if (parsed.rows.length === 0) {
                toast.error('No non-empty rows found in CSV.')
                return
            }
            const newRows: ConfigRow[] = parsed.rows.map((r) => ({
                ...r,
                id: nextId++,
            }))
            setRows(newRows)
        }
        reader.readAsText(file)
        e.target.value = ''
    }

    const onClose = () => {
        setRows([createEmptyRow()])
        setIsSubmitting(false)
        handleClose()
    }

    const handleCreate = async () => {
        if (validation.blocking) {
            return
        }

        const change_order_configs = mergeRowsForBulk(rows)

        setIsSubmitting(true)
        try {
            await dispatch(
                bulkUpsertChangeOrderConfigsRequest({
                    body: { change_order_configs },
                }),
            )
            toast.success(
                `${change_order_configs.length} change order config${
                    change_order_configs.length === 1 ? '' : 's'
                } saved.`,
            )
            onClose()
        } catch {
            // setNetworkError from thunk surfaces the error UI
        } finally {
            setIsSubmitting(false)
        }
    }

    const cellPadding = theme.spacing(1)

    /** Fixed viewport height so the shell does not resize when rows are added or removed. */
    const modalHeight = 'min(720px, max(480px, 85vh))'

    return (
        <Modal
            open={open}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
            onClose={isSubmitting ? undefined : onClose}
        >
            <Slide direction="up" in={open}>
                <Paper
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        outline: 'none',
                        borderRadius: 12,
                        width: 'min(960px, 96vw)',
                        height: modalHeight,
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        minHeight: 0,
                    }}
                >
                    <Container
                        style={{
                            padding: theme.spacing(2, 3),
                            alignItems: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <span
                            style={{
                                ...typographyStyle(theme.typography.h6),
                                fontWeight: 700,
                            }}
                        >
                            Bulk create change order configs
                        </span>
                        <div style={{ flex: 1 }} />
                        <Tooltip title="Download CSV template">
                            <Button
                                startIcon={<GetAppIcon />}
                                onClick={downloadTemplate}
                                disabled={isSubmitting}
                                size="small"
                                style={{
                                    textTransform: 'none',
                                    marginRight: theme.spacing(1),
                                }}
                            >
                                Template CSV
                            </Button>
                        </Tooltip>
                        <Button
                            startIcon={<PublishIcon />}
                            onClick={() => uploadRef.current?.click()}
                            disabled={isSubmitting}
                            size="small"
                            style={{
                                textTransform: 'none',
                                marginRight: theme.spacing(1),
                                color: theme.palette.primary.main,
                            }}
                        >
                            Upload CSV
                        </Button>
                        <input
                            ref={uploadRef}
                            type="file"
                            accept=".csv,text/csv"
                            style={{ display: 'none' }}
                            onChange={handleUpload}
                        />
                        <IconButton
                            size="small"
                            onClick={onClose}
                            disabled={isSubmitting}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Container>

                    <TableContainer
                        style={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                            borderTop: `1px solid ${theme.palette.divider}`,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ fontWeight: 600 }}>
                                        Name
                                        <span
                                            style={{
                                                fontWeight: 400,
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                                fontSize: 12,
                                                marginLeft: 4,
                                            }}
                                        >
                                            (max {NAME_MAX_LEN} characters)
                                        </span>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 600 }}>
                                        Description
                                        <span
                                            style={{
                                                fontWeight: 400,
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                                fontSize: 12,
                                                marginLeft: 4,
                                            }}
                                        >
                                            (max {DESCRIPTION_MAX_LEN}{' '}
                                            characters)
                                        </span>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 600 }}>
                                        Expense
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        style={{ width: 96 }}
                                    />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => {
                                    const nameMissingButRowUsed =
                                        row.name.trim() === '' &&
                                        (row.description.trim() !== '' ||
                                            normalizeExpenseInput(
                                                row.expense,
                                            ) !== '')
                                    const nameOverLimit =
                                        row.name.trim().length > NAME_MAX_LEN
                                    const nameHelper = nameOverLimit
                                        ? `Name must be at most ${NAME_MAX_LEN} characters`
                                        : nameMissingButRowUsed
                                        ? 'Enter a name when description or expense is filled in'
                                        : undefined
                                    const descOverLimit =
                                        row.description.trim().length >
                                        DESCRIPTION_MAX_LEN
                                    const expenseInvalid = rowHasInvalidExpense(
                                        row,
                                    )
                                    return (
                                        <TableRow key={row.id}>
                                            <TableCell
                                                style={{ padding: cellPadding }}
                                            >
                                                <TextField
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    value={row.name}
                                                    disabled={isSubmitting}
                                                    error={
                                                        nameOverLimit ||
                                                        nameMissingButRowUsed
                                                    }
                                                    helperText={nameHelper}
                                                    onChange={(e) =>
                                                        updateRow(
                                                            row.id,
                                                            'name',
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell
                                                style={{ padding: cellPadding }}
                                            >
                                                <TextField
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    value={row.description}
                                                    disabled={isSubmitting}
                                                    error={descOverLimit}
                                                    helperText={
                                                        descOverLimit
                                                            ? `Description must be at most ${DESCRIPTION_MAX_LEN} characters`
                                                            : undefined
                                                    }
                                                    onChange={(e) =>
                                                        updateRow(
                                                            row.id,
                                                            'description',
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell
                                                style={{ padding: cellPadding }}
                                            >
                                                <TextField
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    value={row.expense}
                                                    placeholder="0.00"
                                                    disabled={isSubmitting}
                                                    error={expenseInvalid}
                                                    helperText={
                                                        expenseInvalid
                                                            ? 'Enter a valid number or leave blank'
                                                            : undefined
                                                    }
                                                    onChange={(e) =>
                                                        updateRow(
                                                            row.id,
                                                            'expense',
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        removeRow(row.id)
                                                    }
                                                    disabled={isSubmitting}
                                                    aria-label="remove row"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {showIssueBanner && (
                        <div
                            style={{
                                flexShrink: 0,
                                padding: theme.spacing(1.5, 3),
                                backgroundColor: fade(
                                    theme.palette.error.main,
                                    0.08,
                                ),
                                borderTop: `1px solid ${fade(
                                    theme.palette.error.main,
                                    0.35,
                                )}`,
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                color="error"
                                style={{ fontWeight: 600, marginBottom: 4 }}
                            >
                                Fix the issues below before saving
                            </Typography>
                            <ul
                                style={{
                                    margin: 0,
                                    paddingLeft: theme.spacing(2.5),
                                    ...typographyStyle(theme.typography.body2),
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                {validation.partialRowWithoutName && (
                                    <li>
                                        Rows with a description or expense need
                                        a name.
                                    </li>
                                )}
                                {validation.hasInvalidExpense && (
                                    <li>
                                        One or more expense values are not valid
                                        numbers.
                                    </li>
                                )}
                                {validation.hasNameTooLong && (
                                    <li>
                                        One or more names are longer than{' '}
                                        {NAME_MAX_LEN} characters.
                                    </li>
                                )}
                                {validation.hasDescriptionTooLong && (
                                    <li>
                                        One or more descriptions are longer than{' '}
                                        {DESCRIPTION_MAX_LEN} characters.
                                    </li>
                                )}
                            </ul>
                        </div>
                    )}

                    <Container
                        style={{
                            padding: theme.spacing(2, 3),
                            alignItems: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Button
                            startIcon={<AddIcon />}
                            onClick={addRow}
                            disabled={isSubmitting}
                            size="small"
                            style={{ textTransform: 'none' }}
                        >
                            Add row
                        </Button>
                        <div style={{ flex: 1 }} />
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            disabled={isSubmitting}
                            style={{
                                textTransform: 'none',
                                marginRight: theme.spacing(1),
                            }}
                        >
                            Cancel
                        </Button>
                        {validation.blocking ? (
                            <Tooltip
                                title={
                                    hasAnyUserInput
                                        ? 'Resolve the issues above to save'
                                        : 'Enter at least one name, description, or expense to save'
                                }
                            >
                                <span>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        onClick={handleCreate}
                                        disabled={
                                            isSubmitting || validation.blocking
                                        }
                                        style={{ textTransform: 'none' }}
                                    >
                                        {isSubmitting
                                            ? 'Saving…'
                                            : `Save all (${validation.bulkSaveCount})`}
                                    </Button>
                                </span>
                            </Tooltip>
                        ) : (
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={handleCreate}
                                disabled={isSubmitting}
                                style={{ textTransform: 'none' }}
                            >
                                {isSubmitting
                                    ? 'Saving…'
                                    : `Save all (${validation.bulkSaveCount})`}
                            </Button>
                        )}
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}
